/**
 * Typed helpers for album upload UX on shared `/event/[id]` pages.
 * Server remains authoritative; these mirror API fields for UI only.
 */

export type GuestUploadPolicyInput = {
  /** From event row / API; treat undefined as enabled for backward compatibility. */
  allowGuestPhotoUploads?: boolean
  allowGuestVideoUploads?: boolean
}

export function guestPhotoUploadsEnabled(
  policy: GuestUploadPolicyInput,
): boolean {
  return policy.allowGuestPhotoUploads !== false
}

export function guestVideoUploadsEnabled(
  policy: GuestUploadPolicyInput,
): boolean {
  return policy.allowGuestVideoUploads !== false
}

export type SharedEventUploadCtaInput = GuestUploadPolicyInput & {
  isPublicDemo: boolean
  isUserHosting: boolean
  isPublished: boolean
}

/**
 * Whether a non-host visitor should see contributor-oriented album CTAs
 * (sign-in, attend, or open modal) on the public event page.
 */
export function shouldSharedEventShowUploadCta(
  input: SharedEventUploadCtaInput,
): boolean {
  if (input.isPublicDemo || input.isUserHosting) return false
  if (!input.isPublished) return false
  return (
    guestPhotoUploadsEnabled(input) || guestVideoUploadsEnabled(input)
  )
}

export type ViewerUploadCapabilityInput = {
  isSignedIn: boolean
  canUploadPhotos?: boolean
  canUploadVideos?: boolean
  /** Legacy single flag from older API responses. */
  canUploadMedia?: boolean
}

/** True when the current viewer may upload at least one album asset type. */
export function canCurrentViewerUploadToAlbum(
  input: ViewerUploadCapabilityInput,
): boolean {
  if (!input.isSignedIn) return false
  if (input.canUploadPhotos === true || input.canUploadVideos === true) {
    return true
  }
  if (
    input.canUploadPhotos === false &&
    input.canUploadVideos === false
  ) {
    return false
  }
  return input.canUploadMedia === true
}

/**
 * Theme for non-host `/event/[id]` viewers: bright unless the host enabled dark public appearance.
 */
export function sharedPublicEventThemePreference(
  publicAppearanceDark?: boolean,
): "light" | "dark" {
  return publicAppearanceDark === true ? "dark" : "light"
}
